import { Button, Text } from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'

import logger from '@/utils/logger'

import { useFcmToken } from '@/client/hooks'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'

import ModalV3 from '@/components/UI/Modal/ModalV3'

import type { WebPushNotificationsInterface } from '../../constants'
import useIosIpc from '../useIosIpc'

const MODAL_SCENARIOS = {
  disable_notifications: {
    title: 'Disable Push Notifications',
    description: 'You can disable your Schoolhouse Push Notifications in iOS Settings.',
  },
  denied_permission: {
    title: 'Unable to turn on Notifications',
    description:
      'There was an error while turning on notifications. Please go to iOS Settings for Schoolhouse and turn them on there.',
  },
}

export default function usePushNotifications(): WebPushNotificationsInterface {
  const browserSupportsWebPush = true

  const ipc = useIosIpc()
  const { register: registerFcmToken, clear: clearFcmToken } = useFcmToken()
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false)
  const [settingsModal, setSettingsModal] = useState<keyof typeof MODAL_SCENARIOS | null>(null)

  const handleOpenSettings = useCallback(() => {
    ipc.pushPermissionOpenSettings().catch(err => {
      logger.error('Error opening iOS Settings', { err })
    })
  }, [ipc])

  const handleStateRefresh = useCallback(async () => {
    const permissionState = await ipc.pushPermissionState()

    const enabled = ['authorized', 'ephermal', 'provisional'].includes(permissionState)
    setSubscriptionEnabled(enabled)

    if (enabled) {
      const token = await ipc.pushPermissionToken()
      if (token == null || token === '') {
        clearFcmToken()
        logger.warning('Push token is null even tho we have permissions on iOS')
        return
      }
      registerFcmToken(token)
    } else {
      clearFcmToken()
    }
  }, [ipc, clearFcmToken, registerFcmToken])

  const handleModalClose = useCallback(() => {
    setSettingsModal(null)
    handleStateRefresh().catch(err => {
      logger.error('Error requesting permission state for iOS', { err })
    })
  }, [handleStateRefresh])

  useEffect(() => {
    handleStateRefresh().catch(err => {
      logger.error('Error requesting permission state for iOS', { err })
    })
  }, [handleStateRefresh])

  const [toggleSubscription] = useAPIWrapper(
    async () => {
      const stateBefore = await ipc.pushPermissionState()
      if (stateBefore === 'authorized') {
        setSettingsModal('disable_notifications')
      } else {
        const authorization = await ipc.pushPermissionRequest()
        if (authorization === 'denied') {
          setSettingsModal('denied_permission')
        } else {
          await handleStateRefresh()
        }
      }
    },
    [ipc, handleStateRefresh],
    'Toggle Subscription iOS'
  )

  const element =
    settingsModal == null ? null : (
      <ModalV3
        open
        title={MODAL_SCENARIOS[settingsModal].title}
        onClose={handleModalClose}
        body={() => <Text>{MODAL_SCENARIOS[settingsModal].description}</Text>}
        rightFooterContent={() => <Button onClick={handleOpenSettings}>Open Settings</Button>}
      />
    )

  return {
    element,
    isSupported: browserSupportsWebPush,
    isEnabled: subscriptionEnabled,
    toggleSubscription,
  }
}
